import { createFileRoute } from "@tanstack/react-router";
import { PriorityServiceAreaPage } from "@/components/PriorityServiceAreaPage";
import { seoHead, serviceSchema, localBusinessSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";

const FAQS = [
  { q: "Can I drop my car off before work in Canary Wharf?", a: "Yes — many Canary Wharf customers drop the car early, walk to the office, and collect it after work. We'll WhatsApp you updates." },
  { q: "Do you handle premium brakes (BMW, Mercedes, Audi)?", a: "Yes — we regularly fit OE and premium aftermarket pads/discs (Brembo, ATE, Pagid) on European executive cars. We can also handle electronic parking brake retraction." },
  { q: "How quickly can you fit brake pads and discs?", a: "Pads usually 1–2 hours; pads and discs 2–3 hours. Drop off in the morning and pick up after work." },
  { q: "Do you offer free brake checks?", a: "Yes — free brake inspection with no obligation. We'll show you wear measurements and quote clearly if work is needed." },
];

export const Route = createFileRoute("/canary-wharf/brakes")({
  head: () => seoHead({
    path: "/canary-wharf/brakes",
    title: "Brake Repair near Canary Wharf — Drop-Off Service | Woodseer Autos",
    description: "Brake pads, discs and calipers for Canary Wharf drivers. Drop off before work, collect after. Premium and OE parts.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Canary Wharf", path: "/areas/canary-wharf" },
      { name: "Brakes", path: "/canary-wharf/brakes" },
    ],
    jsonLd: [
      localBusinessSchema({ serviceCoverage: ["Canary Wharf"] }),
      serviceSchema({
        name: "Brake Repair near Canary Wharf",
        serviceType: "Brake repair",
        description: "Brake pad, disc and caliper replacement for cars in Canary Wharf. Drop-off and collect service, premium parts available.",
        areaServed: "Canary Wharf, London",
        url: "https://woodseerautos.co.uk/canary-wharf/brakes",
      }),
      faqSchema(FAQS) as Record<string, unknown>,
    ],
  }),
  component: () => (
    <PriorityServiceAreaPage
      areaSlug="canary-wharf"
      areaName="Canary Wharf"
      serviceSlug="brakes"
      serviceName="Brake Repair"
      parentServiceSlug="mechanical-repairs"
      hero="Brake squeal, judder or a soft pedal on the commute? Drop your car with us before work and collect it after — pads, discs, calipers and fluid all handled in a single day."
      localBenefit="Drop off before work, collect after — designed for Canary Wharf professionals"
      whatsIncluded={[
        "Free brake inspection with measurements",
        "OE and premium pads/discs (Brembo, ATE, Pagid)",
        "Electronic parking brake retraction on European cars",
        "Caliper service or replacement",
        "Brake fluid bleed if needed",
        "WhatsApp updates while you're at work",
      ]}
      commonIssues={[
        "Squealing or grinding on stops",
        "Vibration in the pedal (warped discs)",
        "Pulling under braking",
        "Soft pedal (fluid or air in lines)",
        "Worn pad sensor warning light",
        "Handbrake warning on Audi/BMW/Mercedes",
      ]}
      process={[
        { step: "Drop off", desc: "Leave the car before work — gated yard, no need to wait." },
        { step: "Free inspection", desc: "We measure pad thickness and disc condition and WhatsApp you photos." },
        { step: "Fit same day", desc: "OE-quality or premium parts. Most jobs done by mid-afternoon." },
        { step: "Collect after work", desc: "We'll confirm by WhatsApp when you can collect — usually 5–6pm." },
      ]}
      whyUs={[
        "Tailored drop-off and collect service",
        "WhatsApp updates and photos while you work",
        "Premium parts for European executive cars",
        "Electronic parking brake handling included",
      ]}
      faqs={FAQS}
    />
  ),
});
